import { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import Training from "@/pages/Training";
import ScriptPractice from "@/pages/ScriptPractice";
import { DiscoveryTraining } from "@/components/RolePlay";
import { getImprovementAccess, getImprovementOverview } from "@/lib/api";

// Continuous Improvement: every team's training in one cross-gym view (gym dropdown → Across every gym).
// Overview for the people who run training; then each programme in its own tab.

// Each course is for its own team (enforced on the server too): sales → the sales module, Lead Nurture →
// lead calls, accountability coaches → the coach course. Facility heads and admins oversee them.
const TABS = [
  ["overview", "Overview", null],
  ["sales-training", "Sales training", "sales"],
  ["lead-calls", "Lead call practice", "nurture"],
  ["coach", "Coach practice", "coach"],
];
const ago = (iso) => {
  if (!iso) return "never";
  const d = Math.floor((Date.now() - new Date(iso).getTime()) / 86400000);
  return d <= 0 ? "today" : d === 1 ? "yesterday" : `${d} days ago`;
};

function Progress({ t }) {
  if (!t) return <span className="text-zinc-600">–</span>;
  const done = t.parts_done >= t.parts;
  return (
    <span className={`tabular-nums ${done ? "text-emerald-300" : t.parts_done ? "text-zinc-200" : "text-rose-300"}`}>
      {t.parts_done}/{t.parts}{t.mastered ? <span className="text-zinc-500"> · {t.mastered} mastered</span> : null}
      {t.overdue ? <span className="text-rose-300"> · {t.overdue} behind</span> : null}
      {t.basics != null ? <span className="text-zinc-500"> · basics {t.basics}/6</span> : null}
      {t.certified ? <span className="text-emerald-300"> · certified</span> : null}
    </span>
  );
}

// Sales training holds both halves of the sales course: the live series and the script practice.
function SalesTraining({ view, setView }) {
  return (
    <div className="space-y-4">
      <div className="flex flex-wrap gap-1.5" data-testid="sales-training-views">
        {[["discovery", "Today's training"], ["series", "The series"], ["practice", "Script practice"]].map(([k, label]) => (
          <button key={k} type="button" onClick={() => setView(k)} data-testid={`sales-training-view-${k}`}
            className={`rounded-full px-4 py-1.5 text-sm border ${view === k ? "border-white bg-white text-black" : "border-[#262626] text-zinc-300 hover:border-zinc-500"}`}>{label}</button>
        ))}
      </div>
      {view === "discovery" ? <DiscoveryTraining /> : view === "practice" ? <ScriptPractice key="consult" /> : <Training />}
    </div>
  );
}

function Overview() {
  const [data, setData] = useState(null);
  const [fid, setFid] = useState("");
  const load = useCallback(() => getImprovementOverview(fid || undefined).then(setData)
    .catch((e) => toast.error(e?.response?.data?.detail || "Couldn't load the overview")), [fid]);
  useEffect(() => { load(); }, [load]);
  if (!data) return <div className="flex items-center gap-2 text-zinc-500"><Loader2 className="h-4 w-4 animate-spin" />Loading…</div>;
  const s = data.summary;
  const tiles = [
    ["series", `${s.series.certified}/${s.series.people} certified`, s.series.homework_overdue ? `${s.series.homework_overdue} with homework overdue` : "no overdue homework"],
    ["consult", `${s.consult.finished}/${s.consult.people} finished`, `${s.consult.practising_week} practised this week`],
    ["nurture", `${s.nurture.finished}/${s.nurture.people} finished`, `${s.nurture.practising_week} practised this week`],
    ["accountability", `${s.accountability.finished}/${s.accountability.people} finished · ${s.accountability.certified || 0} certified`, `${s.accountability.practising_week} practised this week`],
  ];
  return (
    <div className="space-y-5" data-testid="improvement-overview">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-sm text-zinc-400">Showing {data.scope}. A part is finished once its role play (level 4) is passed, at {data.pass_mark} or more.</p>
        {data.can_filter && (
          <select value={fid} onChange={(e) => setFid(e.target.value)} className="h-9 rounded-md bg-[#0a0a0a] border border-[#262626] px-2 text-sm" data-testid="improvement-gym">
            <option value="">Every gym</option>
            {data.facilities.map((f) => <option key={f.id} value={f.id}>{f.name}</option>)}
          </select>
        )}
      </div>
      <div className="grid gap-3 grid-cols-1 sm:grid-cols-2 xl:grid-cols-4">
        {tiles.map(([k, big, small]) => (
          <div key={k} className="card-surface p-4">
            <div className="eyebrow">{s[k].label}</div>
            <div className="font-display text-2xl font-bold mt-1 tabular-nums">{big}</div>
            <div className="text-sm text-zinc-400">{small}</div>
          </div>
        ))}
      </div>
      <div className="card-surface overflow-x-auto">
        <table className="w-full text-sm min-w-[860px]">
          <thead><tr className="text-left text-xs uppercase tracking-wide text-zinc-500">
            <th className="p-3">Person</th><th className="p-3">Gym</th><th className="p-3">Sales series</th><th className="p-3">Sales script</th>
            <th className="p-3">Lead calls</th><th className="p-3">Coach course</th><th className="p-3">Role plays this week</th><th className="p-3">Last practice</th>
          </tr></thead>
          <tbody>
            {!data.rows.length && <tr><td colSpan={8} className="p-4 text-zinc-500">Nobody here is on a training track yet.</td></tr>}
            {data.rows.map((r) => (
              <tr key={r.user_id} className="border-t border-[#1f1f1f]">
                <td className="p-3 font-medium">{r.name}</td>
                <td className="p-3 text-zinc-400">{r.gym || "–"}</td>
                <td className="p-3">{r.sales_series ? (
                  <span className="tabular-nums">{r.sales_series.attended}/{r.sales_series.sessions} sessions
                    {r.sales_series.certified ? <span className="text-emerald-300"> · certified</span> : null}
                    {r.sales_series.homework_overdue ? <span className="text-rose-300"> · {r.sales_series.homework_overdue} overdue</span> : null}</span>
                ) : <span className="text-zinc-600">–</span>}</td>
                <td className="p-3"><Progress t={r.tracks.consult} /></td>
                <td className="p-3"><Progress t={r.tracks.nurture} /></td>
                <td className="p-3"><Progress t={r.tracks.accountability} /></td>
                <td className="p-3 tabular-nums">{r.role_plays_week || <span className="text-zinc-600">0</span>}</td>
                <td className={`p-3 ${r.last_practice && Date.now() - new Date(r.last_practice).getTime() < 7 * 86400000 ? "text-zinc-300" : "text-rose-300"}`}>{ago(r.last_practice)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default function Improvement() {
  const [params, setParams] = useSearchParams();
  const [access, setAccess] = useState(null);
  useEffect(() => { getImprovementAccess().then(setAccess).catch(() => setAccess({})); }, []);
  if (!access) return <div className="flex items-center gap-2 text-zinc-500"><Loader2 className="h-4 w-4 animate-spin" />Loading…</div>;
  const courses = TABS.filter(([, , need]) => need && access[need]);
  const tabs = [["overview", access.overview ? "Overview" : "My progress", null], ...courses];
  const old = params.get("tab") === "script";                     // old links: Script practice now sits in Sales training
  const wanted = old ? "sales-training" : params.get("tab");
  const view = old || params.get("view") === "practice" ? "practice" : params.get("view") === "series" ? "series" : "discovery";
  const tab = tabs.some(([k]) => k === wanted) ? wanted : (access.overview || !courses.length ? "overview" : courses[0][0]);
  return (
    <div className="space-y-5" data-testid="improvement-page">
      <div>
        <div className="eyebrow">Across every gym</div>
        <h1 className="font-display text-2xl sm:text-3xl font-extrabold mt-1">Continuous Improvement</h1>
        <p className="text-sm text-zinc-400 mt-1 max-w-2xl">Training and practice for every team: the sales training series with its script practice, lead call practice and the coach course.</p>
      </div>
      <div className="flex gap-1 overflow-x-auto border-b border-[#1f1f1f]">
        {tabs.map(([k, label]) => (
          <button key={k} type="button" onClick={() => setParams({ tab: k })} data-testid={`improvement-tab-${k}`}
            className={`px-4 py-2 text-sm -mb-px border-b-2 whitespace-nowrap ${tab === k ? "border-emerald-500 text-white" : "border-transparent text-zinc-400 hover:text-zinc-200"}`}>{label}</button>
        ))}
      </div>
      {!courses.length && (
        <p className="text-sm text-amber-300">You're not on a course yet. Courses follow your role: sales, Lead Nurture or accountability coaching. Ask your facility head to set your skills in User Management.</p>
      )}
      {wanted && !tabs.some(([k]) => k === wanted) && (
        <p className="text-sm text-zinc-400">That course is for another team, so you're seeing your own instead.</p>
      )}
      {tab === "overview" && <Overview />}
      {tab === "sales-training" && <SalesTraining view={view} setView={(v) => setParams(v === "discovery" ? { tab } : { tab, view: v })} />}
      {tab === "lead-calls" && <ScriptPractice key="nurture" track="nurture" />}
      {tab === "coach" && <ScriptPractice key="accountability" track="accountability" />}
    </div>
  );
}
